(() => {
  'use strict';

  if (window.__KSS_SEARCH_V1_LOADED__) return;
  window.__KSS_SEARCH_V1_LOADED__ = true;

  const SELECTORS = {
    shell: '[data-kss-shell]',
    form: '[data-kss-form]',
    input: '[data-kss-input]',
    clear: '[data-kss-clear]',
    starter: '[data-kss-starter]',
    results: '[data-kss-results]',
    loading: '[data-kss-loading]',
    status: '[data-kss-status]',
    predictiveRoot: '[data-kss-predictive-root]',
    page: '[data-kss-page]',
    pageLoading: '[data-kss-page-loading]',
    filterShell: '[data-kss-filter-shell]',
    filterPanel: '[data-kss-filter-panel]',
    filterOpen: '[data-kss-filter-open]',
    filterClose: '[data-kss-filter-close]',
    filterForm: '[data-kss-filter-form]',
    filterMore: '[data-kss-filter-more]',
    filterList: '[data-kss-filter-list]',
    sortForm: '[data-kss-sort-form]',
    sortSelect: '[data-kss-sort-select]',
    ajaxLink: '[data-kss-ajax-link]',
    grid: '[data-kss-grid]',
    gridItem: '[data-kss-grid-item]',
    pagination: '[data-kss-pagination]',
    loadMore: '[data-kss-load-more]',
    loadMoreLabel: '[data-kss-load-more-label]',
    loadedCount: '[data-kss-loaded-count]',
    resultCount: '[data-kss-result-count]',
    formattedNumber: '[data-kss-number]',
  };

  const UTILITY_SELECTORS = [
    '#vanchat-root',
    '#ShopifyChat',
    '[data-shopify-chat]',
    'iframe[title*="chat" i]',
    '.lang-curentcy',
    'localization-form',
    '.shopify-localization-form',
    '.gtranslate_wrapper',
    '#gtranslate_wrapper',
    '.gt_switcher_wrapper',
    '.gt_switcher',
    '.gt_float_switcher',
    '.gt_container',
    '.gt_selector',
    '.switcher.notranslate',
    '[class*="gt_switcher"]',
    '[class*="gtranslate"]',
    'iframe[src*="gtranslate"]',
    'iframe[title*="translate" i]',
    'iframe[title*="language" i]',
    '.ly-switcher-wrapper',
    '.weglot-container',
    '.weglot_switcher',
    '#google_translate_element',
    '.tlab-switcher',
    '#transcySwitcherContainer',
    '.transcy-switcher-manual',
    '.etranslate-language-switcher',
    '.langify-switcher',
  ].join(',');

  const predictiveCache = new Map();
  const shellState = new WeakMap();
  const pageState = new WeakMap();
  const desktopQuery = window.matchMedia('(min-width: 990px)');

  /*
   * Small, intentional vocabulary bridge for terms customers commonly use
   * differently from supplier product titles. Keep this list conservative:
   * it changes only exact phrases and never rewrites partial words.
   */
  const SEARCH_ALIASES = new Map([
    ['swimwear', 'swimsuit'],
    ['swim wear', 'swimsuit'],
    ['beachwear', 'swimsuit'],
    ['beach wear', 'swimsuit'],
    ['swimsuits', 'swimsuit'],
    ['plushies', 'plush'],
    ['plush toys', 'plush'],
    ['stuffed animal', 'plush'],
    ['stuffed animals', 'plush'],
  ]);

  let pageNavigationController = null;
  let globalListenersReady = false;

  const debounce = (callback, delay = 180) => {
    let timer = 0;

    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => callback(...args), delay);
    };
  };

  const sameOriginUrl = (value) => {
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin ? url : null;
    } catch (_error) {
      return null;
    }
  };

  const cleanUrl = (value) => {
    const url = sameOriginUrl(value);
    if (!url) return null;
    url.searchParams.delete('section_id');
    return url;
  };

  const normalizeSearchQuery = (value) =>
    String(value || '')
      .trim()
      .replace(/\s+/g, ' ')
      .toLowerCase();

  const resolveSearchQuery = (value) => {
    const normalized = normalizeSearchQuery(value);
    return SEARCH_ALIASES.get(normalized) || normalized;
  };

  const resolvedFormUrl = (form, resolvedQuery) => {
    const url = sameOriginUrl(form?.action || window.location.href);
    if (!url || !form) return null;

    url.search = '';

    new FormData(form).forEach((value, key) => {
      if (key === 'q') return;
      const cleanValue = String(value).trim();
      if (cleanValue !== '') url.searchParams.append(key, cleanValue);
    });

    url.searchParams.set('q', resolvedQuery);
    return url;
  };

  const sectionUrl = (value, sectionId) => {
    const url = cleanUrl(value);
    if (!url) return null;
    url.searchParams.set('section_id', sectionId);
    return url;
  };

  const formUrl = (form) => {
    const url = sameOriginUrl(form?.action || window.location.href);
    if (!url || !form) return null;

    url.search = '';

    new FormData(form).forEach((value, key) => {
      const cleanValue = String(value).trim();
      if (cleanValue !== '') url.searchParams.append(key, cleanValue);
    });

    url.searchParams.delete('page');
    return url;
  };

  const formatNumber = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return String(value ?? '');

    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || 'en-US',
      ).format(number);
    } catch (_error) {
      return String(number);
    }
  };

  const formatNumbers = (scope = document) => {
    scope.querySelectorAll?.(SELECTORS.formattedNumber).forEach((element) => {
      const value = element.dataset.kssNumber || element.textContent;
      element.textContent = formatNumber(value);
    });
  };

  const hydrateRatings = (scope = document) => {
    try {
      window.BlsReloadSpr?.init?.();
      window.SPR?.loadBadges?.();
    } catch (_error) {
      // Review apps can initialize asynchronously. The markup remains valid.
    }

    document.dispatchEvent(
      new CustomEvent('kss:search-content-rendered', {
        detail: { scope },
      }),
    );
  };

  const suppressUtilities = (suppress) => {
    document.querySelectorAll(UTILITY_SELECTORS).forEach((element) => {
      element.classList.toggle('kss-suppressed-utility', suppress);
    });
  };

  const cartDrawerIsOpen = () =>
    Boolean(
      document.querySelector(
        '#bls-header_minicart.bls__opend-popup-header',
      ),
    );

  const syncCartLayerState = () => {
    const isOpen = cartDrawerIsOpen();
    document.documentElement.classList.toggle('kss-cart-open', isOpen);
    document.body?.classList.toggle('kss-cart-open', isOpen);

    if (!isOpen) return;

    document.querySelectorAll('.kss-shell--inline.is-active').forEach((shell) => {
      shell.classList.remove('is-active');
      shell.querySelector(SELECTORS.input)?.blur();
    });
  };

  const syncGlobalSearchState = () => {
    const searchHost = document.querySelector('#search-form');
    const inlineOpen = Boolean(
      document.querySelector('.kss-shell--inline.is-active'),
    );
    const isOpen = Boolean(
      !cartDrawerIsOpen() &&
        (inlineOpen ||
          searchHost?.classList.contains('bls__opend-popup-header') ||
          document.documentElement.classList.contains('open-search')),
    );

    document.documentElement.classList.toggle('kss-search-open', isOpen);
    document.body?.classList.toggle('kss-search-open', isOpen);
    document.querySelectorAll('.top-search-toggle').forEach((button) => {
      button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
    suppressUtilities(isOpen || document.documentElement.classList.contains('kss-filter-open'));

    if (isOpen) {
      window.setTimeout(() => {
        const visibleShell = [...document.querySelectorAll(SELECTORS.shell)].find(
          (shell) => shell.getClientRects().length > 0,
        );
        visibleShell?.querySelector(SELECTORS.input)?.focus({ preventScroll: true });
      }, 80);
    }
  };

  const closeGlobalSearch = () => {
    const searchHost = document.querySelector('#search-form');
    searchHost?.classList.remove('bls__opend-popup-header');
    document.documentElement.classList.remove('hside_opened', 'open-search');
    syncGlobalSearchState();
  };

  const setPredictiveLoading = (shell, loading) => {
    const results = shell.querySelector(SELECTORS.results);
    const loadingElement = shell.querySelector(SELECTORS.loading);

    shell.classList.toggle('is-loading', loading);
    loadingElement.hidden = !loading;
    results?.setAttribute('aria-busy', loading ? 'true' : 'false');
  };

  const resetPredictiveSelection = (shell) => {
    shell.querySelectorAll('[role="option"][aria-selected="true"]').forEach((option) => {
      option.setAttribute('aria-selected', 'false');
    });

    const input = shell.querySelector(SELECTORS.input);
    input?.removeAttribute('aria-activedescendant');

    const state = shellState.get(shell);
    if (state) state.activeIndex = -1;
  };

  const showStarter = (shell) => {
    const starter = shell.querySelector(SELECTORS.starter);
    const results = shell.querySelector(SELECTORS.results);
    const input = shell.querySelector(SELECTORS.input);

    resetPredictiveSelection(shell);
    shell.classList.remove('has-results');
    starter.hidden = false;
    results.hidden = true;
    results.innerHTML = '';
    input.setAttribute('aria-expanded', 'false');
    setPredictiveLoading(shell, false);

    /*
     * Starter-state products are already in the page when the search shell
     * opens, so they do not pass through renderPredictiveMarkup(). Apply the
     * same image decode/reveal lifecycle here to prevent permanent skeletons.
     */
    preparePredictiveImages(starter);
  };

  const predictiveOptions = (shell) =>
    [...shell.querySelectorAll(`${SELECTORS.results} [role="option"]`)].filter(
      (element) => !element.hasAttribute('disabled') && element.getClientRects().length > 0,
    );

  const selectPredictiveOption = (shell, nextIndex) => {
    const options = predictiveOptions(shell);
    if (!options.length) return;

    const state = shellState.get(shell);
    const input = shell.querySelector(SELECTORS.input);
    const normalizedIndex = (nextIndex + options.length) % options.length;

    options.forEach((option, index) => {
      option.setAttribute('aria-selected', index === normalizedIndex ? 'true' : 'false');
    });

    const active = options[normalizedIndex];
    if (!active.id) active.id = `KssOption-${Date.now()}-${normalizedIndex}`;

    state.activeIndex = normalizedIndex;
    input.setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView({ block: 'nearest' });
  };

  const preparePredictiveImages = (scope) => {
    scope.querySelectorAll('.kss-mini-product__media').forEach((frame) => {
      if (frame.classList.contains('is-image-ready')) return;

      const image = frame.querySelector('.kss-mini-product__image');
      const reveal = () => {
        frame.classList.add('is-image-ready');
        frame.removeAttribute('aria-busy');
      };

      if (!image) {
        reveal();
        return;
      }

      frame.setAttribute('aria-busy', 'true');

      if (image.complete) {
        if (image.naturalWidth > 0 && typeof image.decode === 'function') {
          image.decode().catch(() => {}).finally(reveal);
        } else {
          reveal();
        }
        return;
      }

      image.addEventListener('load', reveal, { once: true });
      image.addEventListener('error', reveal, { once: true });
    });
  };

  const renderPredictiveMarkup = (shell, markup, query, resolvedQuery = query) => {
    const results = shell.querySelector(SELECTORS.results);
    const starter = shell.querySelector(SELECTORS.starter);
    const status = shell.querySelector(SELECTORS.status);
    const input = shell.querySelector(SELECTORS.input);

    results.innerHTML = markup;
    results.hidden = false;
    starter.hidden = true;
    shell.classList.add('has-results');
    input.setAttribute('aria-expanded', 'true');
    resetPredictiveSelection(shell);
    setPredictiveLoading(shell, false);

    const predictiveRoot = results.querySelector(SELECTORS.predictiveRoot);
    const resultCount = Number(predictiveRoot?.dataset.resultCount || 0);
    status.textContent = resultCount
      ? `${resultCount} suggestions found for ${query}.`
      : `No suggestions found for ${query}.`;

    const viewAll = results.querySelector('.kss-view-all');
    if (viewAll) {
      const viewAllUrl = sameOriginUrl(viewAll.href);
      if (viewAllUrl) {
        viewAllUrl.searchParams.set('q', resolvedQuery);
        viewAll.href = viewAllUrl.toString();
      }

      const label = viewAll.querySelector('span');
      if (label) label.textContent = `View all product results for “${query}”`;
    }

    preparePredictiveImages(results);
    hydrateRatings(results);
  };

  const fetchPredictiveResults = async (shell, query) => {
    const state = shellState.get(shell);
    const normalizedQuery = normalizeSearchQuery(query);
    const resolvedQuery = resolveSearchQuery(query);
    const cacheKey = `${normalizedQuery}::${resolvedQuery}`;

    if (predictiveCache.has(cacheKey)) {
      renderPredictiveMarkup(
        shell,
        predictiveCache.get(cacheKey),
        query,
        resolvedQuery,
      );
      return;
    }

    state.requestController?.abort();
    state.requestController = new AbortController();
    setPredictiveLoading(shell, true);

    const predictiveUrl = sameOriginUrl(shell.dataset.predictiveUrl);
    if (!predictiveUrl) {
      setPredictiveLoading(shell, false);
      return;
    }

    predictiveUrl.searchParams.set('q', resolvedQuery);
    predictiveUrl.searchParams.set(
      'resources[type]',
      'query,product,collection,page,article',
    );
    predictiveUrl.searchParams.set('resources[limit]', '4');
    predictiveUrl.searchParams.set('resources[limit_scope]', 'each');
    predictiveUrl.searchParams.set(
      'resources[options][unavailable_products]',
      'last',
    );
    predictiveUrl.searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,tag',
    );
    predictiveUrl.searchParams.set('section_id', shell.dataset.sectionId);

    try {
      const response = await fetch(predictiveUrl.toString(), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: state.requestController.signal,
      });

      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

      const text = await response.text();
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const section = parsed.getElementById(
        `shopify-section-${shell.dataset.sectionId}`,
      );
      const markup = section?.innerHTML?.trim();

      if (!markup) throw new Error('Predictive search markup was not found.');

      predictiveCache.set(cacheKey, markup);
      renderPredictiveMarkup(shell, markup, query, resolvedQuery);
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error('[Kawaii Stop search]', error);
      setPredictiveLoading(shell, false);

      const status = shell.querySelector(SELECTORS.status);
      status.textContent = 'Search suggestions are temporarily unavailable. Press Enter to search.';
    }
  };

  const initPredictiveShell = (shell) => {
    if (shell.dataset.kssInitialized === 'true') return;
    shell.dataset.kssInitialized = 'true';

    const form = shell.querySelector(SELECTORS.form);
    const input = shell.querySelector(SELECTORS.input);
    const clear = shell.querySelector(SELECTORS.clear);
    const state = {
      activeIndex: -1,
      requestController: null,
    };
    shellState.set(shell, state);

    const search = debounce(() => {
      const query = input.value.trim();
      if (query.length < 2) {
        showStarter(shell);
        return;
      }
      fetchPredictiveResults(shell, query);
    }, 180);

    const isInline = shell.dataset.kssMode === 'inline';
    const activateInline = () => {
      if (!isInline || cartDrawerIsOpen()) return;
      shell.classList.add('is-active');
      syncGlobalSearchState();
    };
    const closeInline = () => {
      if (!isInline) return;
      shell.classList.remove('is-active');
      resetPredictiveSelection(shell);
      syncGlobalSearchState();
    };

    input.addEventListener('input', () => {
      activateInline();
      clear.hidden = input.value.length === 0;
      search();
    });

    input.addEventListener('focus', () => {
      activateInline();
      const query = input.value.trim();
      if (query.length >= 2 && shell.querySelector(SELECTORS.results).hidden) {
        fetchPredictiveResults(shell, query);
      }
    });

    input.addEventListener('keydown', (event) => {
      const options = predictiveOptions(shell);

      if (event.key === 'ArrowDown' && options.length) {
        event.preventDefault();
        selectPredictiveOption(shell, state.activeIndex + 1);
        return;
      }

      if (event.key === 'ArrowUp' && options.length) {
        event.preventDefault();
        selectPredictiveOption(shell, state.activeIndex - 1);
        return;
      }

      if (event.key === 'Enter' && state.activeIndex >= 0) {
        const active = options[state.activeIndex];
        if (active) {
          event.preventDefault();
          active.click();
        }
        return;
      }

      if (event.key === 'Escape') {
        if (isInline) {
          event.preventDefault();
          closeInline();
          input.blur();
          return;
        }

        if (!shell.querySelector(SELECTORS.results).hidden) {
          event.preventDefault();
          showStarter(shell);
          return;
        }

        shell.querySelector('.button-close')?.click();
      }
    });

    form.addEventListener('submit', (event) => {
      const originalQuery = input.value.trim();
      if (!originalQuery) {
        event.preventDefault();
        input.focus();
        return;
      }

      const resolvedQuery = resolveSearchQuery(originalQuery);
      if (resolvedQuery === normalizeSearchQuery(originalQuery)) return;

      const targetUrl = resolvedFormUrl(form, resolvedQuery);
      if (!targetUrl) return;

      event.preventDefault();
      window.location.assign(targetUrl.toString());
    });

    clear.addEventListener('click', () => {
      input.value = '';
      clear.hidden = true;
      state.requestController?.abort();
      showStarter(shell);
      input.focus();
    });

    shell.addEventListener('pointerover', (event) => {
      const option = event.target.closest('[role="option"]');
      if (!option || !shell.contains(option)) return;
      const options = predictiveOptions(shell);
      const index = options.indexOf(option);
      if (index >= 0) selectPredictiveOption(shell, index);
    });

    clear.hidden = input.value.length === 0;
    showStarter(shell);
  };

  const setPageLoading = (root, loading) => {
    root.classList.toggle('is-updating', loading);
    root.setAttribute('aria-busy', loading ? 'true' : 'false');
    const indicator = root.querySelector(SELECTORS.pageLoading);
    if (indicator) indicator.hidden = !loading;
  };

  const setFilterPageState = (open) => {
    document.documentElement.classList.toggle('kss-filter-open', open);
    document.body?.classList.toggle('kss-filter-open', open);
    suppressUtilities(open || document.documentElement.classList.contains('kss-search-open'));
  };

  const syncFilterAccessibility = (root) => {
    const shell = root.querySelector(SELECTORS.filterShell);
    const panel = root.querySelector(SELECTORS.filterPanel);
    const trigger = root.querySelector(SELECTORS.filterOpen);
    if (!shell || !panel) return;

    const open = shell.classList.contains('is-open');

    if (desktopQuery.matches) {
      shell.classList.remove('is-open');
      shell.removeAttribute('aria-hidden');
      panel.setAttribute('role', 'region');
      panel.removeAttribute('aria-modal');
      trigger?.setAttribute('aria-expanded', 'false');
      setFilterPageState(false);
      return;
    }

    shell.setAttribute('aria-hidden', open ? 'false' : 'true');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    trigger?.setAttribute('aria-expanded', open ? 'true' : 'false');
  };

  const openFilters = (root) => {
    const shell = root.querySelector(SELECTORS.filterShell);
    if (!shell || desktopQuery.matches) return;

    shell.classList.add('is-open');
    syncFilterAccessibility(root);
    setFilterPageState(true);

    window.setTimeout(() => {
      shell.querySelector(SELECTORS.filterClose)?.focus();
    }, 60);
  };

  const closeFilters = (root, restoreFocus = true) => {
    const shell = root.querySelector(SELECTORS.filterShell);
    if (!shell) return;

    const wasOpen = shell.classList.contains('is-open');
    shell.classList.remove('is-open');
    syncFilterAccessibility(root);
    setFilterPageState(false);

    if (wasOpen && restoreFocus) root.querySelector(SELECTORS.filterOpen)?.focus();
  };

  const parseNextSearchPage = (text, sectionId) => {
    const parsed = new DOMParser().parseFromString(text, 'text/html');
    const section = parsed.getElementById(`shopify-section-${sectionId}`);
    return section?.querySelector(SELECTORS.page) || parsed.querySelector(SELECTORS.page);
  };

  const replaceSearchPage = async (
    root,
    targetUrl,
    { updateHistory = true, replaceHistory = false, focusCount = true } = {},
  ) => {
    const sectionId = root.dataset.sectionId;
    const requestUrl = sectionUrl(targetUrl, sectionId);
    const browserUrl = cleanUrl(targetUrl);
    if (!requestUrl || !browserUrl) return;

    pageNavigationController?.abort();
    pageNavigationController = new AbortController();
    setPageLoading(root, true);

    try {
      const response = await fetch(requestUrl.toString(), {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: pageNavigationController.signal,
      });

      if (!response.ok) throw new Error(`Search update failed: ${response.status}`);

      const text = await response.text();
      const nextRoot = parseNextSearchPage(text, sectionId);
      if (!nextRoot) throw new Error('Updated search markup was not found.');

      closeFilters(root, false);
      root.replaceWith(nextRoot);
      initSearchPage(nextRoot);
      formatNumbers(nextRoot);
      hydrateRatings(nextRoot);

      if (updateHistory) {
        if (replaceHistory) {
          window.history.replaceState({ kssSearch: true }, '', browserUrl.toString());
        } else {
          window.history.pushState({ kssSearch: true }, '', browserUrl.toString());
        }
      }

      if (focusCount) {
        nextRoot.querySelector(SELECTORS.resultCount)?.focus({ preventScroll: true });
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[Kawaii Stop search]', error);
      window.location.assign(browserUrl.toString());
    } finally {
      const currentRoot = document.querySelector(SELECTORS.page);
      if (currentRoot) setPageLoading(currentRoot, false);
    }
  };

  const loadMoreResults = async (root, button) => {
    const nextUrl = button.dataset.nextUrl;
    const sectionId = root.dataset.sectionId;
    const requestUrl = sectionUrl(nextUrl, sectionId);
    if (!requestUrl) return;

    button.classList.add('is-loading');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(requestUrl.toString(), {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) throw new Error(`Load more failed: ${response.status}`);

      const text = await response.text();
      const nextRoot = parseNextSearchPage(text, sectionId);
      const currentGrid = root.querySelector(SELECTORS.grid);
      const nextGrid = nextRoot?.querySelector(SELECTORS.grid);
      const currentPagination = root.querySelector(SELECTORS.pagination);
      const nextPagination = nextRoot?.querySelector(SELECTORS.pagination);

      if (!currentGrid || !nextGrid || !currentPagination || !nextPagination) {
        throw new Error('The next result batch was incomplete.');
      }

      const fragment = document.createDocumentFragment();
      [...nextGrid.querySelectorAll(`:scope > ${SELECTORS.gridItem}`)].forEach((item) => {
        fragment.appendChild(item);
      });
      currentGrid.appendChild(fragment);
      currentPagination.replaceWith(nextPagination);

      const browserUrl = cleanUrl(nextUrl);
      if (browserUrl) window.history.replaceState({ kssSearch: true }, '', browserUrl.toString());

      formatNumbers(root);
      hydrateRatings(root);
    } catch (error) {
      console.error('[Kawaii Stop search]', error);
      button.classList.remove('is-loading');
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.querySelector(SELECTORS.loadMoreLabel).textContent = 'Try again';
    }
  };

  const initSearchPage = (root) => {
    if (!root || root.dataset.kssInitialized === 'true') return;
    root.dataset.kssInitialized = 'true';

    const controller = new AbortController();
    const { signal } = controller;
    pageState.set(root, controller);

    root.addEventListener(
      'click',
      (event) => {
        const filterOpen = event.target.closest(SELECTORS.filterOpen);
        if (filterOpen) {
          event.preventDefault();
          openFilters(root);
          return;
        }

        const filterClose = event.target.closest(SELECTORS.filterClose);
        if (filterClose) {
          event.preventDefault();
          closeFilters(root);
          return;
        }

        const more = event.target.closest(SELECTORS.filterMore);
        if (more) {
          event.preventDefault();
          const list = more.previousElementSibling?.matches(SELECTORS.filterList)
            ? more.previousElementSibling
            : more.closest('.kss-filter-group__content')?.querySelector(SELECTORS.filterList);
          if (!list) return;

          const expanded = list.classList.toggle('is-expanded');
          more.setAttribute('aria-expanded', expanded ? 'true' : 'false');
          const label = more.querySelector('[data-kss-filter-more-label]');
          if (label) label.textContent = expanded ? 'Show less' : 'Show more';
          return;
        }

        const ajaxLink = event.target.closest(SELECTORS.ajaxLink);
        if (ajaxLink) {
          const url = sameOriginUrl(ajaxLink.href);
          if (!url) return;
          event.preventDefault();
          replaceSearchPage(root, url.toString());
          return;
        }

        const loadMore = event.target.closest(SELECTORS.loadMore);
        if (loadMore) {
          event.preventDefault();
          loadMoreResults(root, loadMore);
        }
      },
      { signal },
    );

    root.addEventListener(
      'submit',
      (event) => {
        const filterForm = event.target.closest(SELECTORS.filterForm);
        if (!filterForm) return;

        const url = formUrl(filterForm);
        if (!url) return;

        event.preventDefault();
        replaceSearchPage(root, url.toString());
      },
      { signal },
    );

    root.addEventListener(
      'change',
      (event) => {
        const select = event.target.closest(SELECTORS.sortSelect);
        if (!select) return;

        const sortForm = select.closest(SELECTORS.sortForm);
        const url = formUrl(sortForm);
        if (!url) return;

        replaceSearchPage(root, url.toString());
      },
      { signal },
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && root.querySelector(SELECTORS.filterShell)?.classList.contains('is-open')) {
          event.preventDefault();
          closeFilters(root);
        }
      },
      { signal },
    );

    const updateBreakpoint = () => closeFilters(root, false);
    desktopQuery.addEventListener('change', updateBreakpoint, { signal });

    syncFilterAccessibility(root);
    formatNumbers(root);
    hydrateRatings(root);
  };

  const initGlobalListeners = () => {
    if (globalListenersReady) return;
    globalListenersReady = true;

    document.addEventListener('click', (event) => {
      if (event.target.closest('.top-search-toggle')) {
        window.setTimeout(syncGlobalSearchState, 0);
      }

      if (event.target.closest('.kss-shell .button-close')) {
        event.preventDefault();
        closeGlobalSearch();
      }

      let inlineChanged = false;
      document.querySelectorAll('.kss-shell--inline.is-active').forEach((shell) => {
        if (!shell.contains(event.target)) {
          shell.classList.remove('is-active');
          inlineChanged = true;
        }
      });

      if (inlineChanged) syncGlobalSearchState();
    });

    window.addEventListener('popstate', () => {
      const root = document.querySelector(SELECTORS.page);
      if (root) {
        replaceSearchPage(root, window.location.href, {
          updateHistory: false,
          focusCount: false,
        });
      }
    });

    const searchHost = document.querySelector('#search-form');
    if (searchHost) {
      new MutationObserver(syncGlobalSearchState).observe(searchHost, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    const cartDrawer = document.querySelector('#bls-header_minicart');
    if (cartDrawer) {
      new MutationObserver(() => {
        syncCartLayerState();
        syncGlobalSearchState();
      }).observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    /*
     * Umino toggles hside_opened on <html> whenever a global drawer changes.
     * Observe the document element as the stable source of truth because the
     * mini-cart section itself can be replaced after an AJAX cart refresh.
     * The callback still verifies the actual cart drawer class, so search and
     * other Umino side panels are not misclassified as the cart.
     */
    new MutationObserver(() => {
      syncCartLayerState();
      syncGlobalSearchState();
    }).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    new MutationObserver(() => {
      if (
        document.documentElement.classList.contains('kss-search-open') ||
        document.documentElement.classList.contains('kss-filter-open')
      ) {
        suppressUtilities(true);
      }
    }).observe(document.body, { childList: true, subtree: true });

    syncCartLayerState();
    syncGlobalSearchState();
  };

  const init = (scope = document) => {
    scope.querySelectorAll?.(SELECTORS.shell).forEach(initPredictiveShell);
    scope.querySelectorAll?.(SELECTORS.page).forEach(initSearchPage);
    initGlobalListeners();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
